import { useMemo, useState } from "react";
import { Icon } from "../../components/Icon";
import {
  Button,
  Card,
  Pill,
  ProgressBar,
  SectionHeading,
} from "../../components/ui";
import { GUILDS } from "../../data/seed";
import { downloadCsv } from "../../lib/csv";
import { masteryFromEvidence, toStudentAnalytics } from "../../lib/progression";
import { usePortal } from "../../state/PortalContext";
import { getCourseRatio } from "./FacultyHomePage";

export function FacultyAnalyticsPage() {
  const { cohort, curriculum, students, studentProgress } = usePortal();
  const [selectedId, setSelectedId] = useState(students[0]?.id);
  const [riskOnly, setRiskOnly] = useState(false);
  const courseRatio = getCourseRatio(cohort.startsAt, cohort.endsAt);
  const analytics = useMemo(
    () =>
      students.map((student) =>
        toStudentAnalytics(
          student,
          studentProgress[student.id],
          cohort.riskRules,
          courseRatio,
        ),
      ),
    [cohort.riskRules, courseRatio, studentProgress, students],
  );
  const visible = riskOnly
    ? analytics.filter((item) => item.riskReasons.length > 0)
    : analytics;
  const selected = analytics.find((item) => item.student.id === selectedId);
  const selectedProgress = selected
    ? studentProgress[selected.student.id]
    : undefined;

  const exportGradebook = () => {
    const gradedQuests = curriculum.quests.filter(
      (quest) => quest.templateId !== "coming-soon",
    );
    const rows: Array<Array<string | number>> = [
      [
        "Student",
        "Email",
        ...gradedQuests.map((quest) => quest.title),
        "Total Earned",
        "Total Possible",
        "Completion %",
        "Mastery Average",
      ],
      ...analytics.map((item) => {
        const progress = studentProgress[item.student.id];
        const questPoints = gradedQuests.map((quest) =>
          Math.max(
            0,
            ...progress.attempts
              .filter(
                (attempt) =>
                  attempt.questId === quest.id &&
                  attempt.status === "succeeded",
              )
              .map((attempt) => attempt.awardedGradePoints),
          ),
        );
        return [
          item.student.fullName,
          item.student.email,
          ...questPoints,
          questPoints.reduce((sum, value) => sum + value, 0),
          gradedQuests.reduce((sum, quest) => sum + quest.gradePoints, 0),
          item.completion,
          item.masteryAverage,
        ];
      }),
    ];
    downloadCsv(`mathportal-${cohort.term}-gradebook.csv`, rows);
  };

  const regionAverages = curriculum.regions.map((region) => ({
    region,
    average: Math.round(
      students.reduce(
        (sum, student) =>
          sum + (studentProgress[student.id]?.regionCompletion[region.id] ?? 0),
        0,
      ) / Math.max(1, students.length),
    ),
  }));
  const competencyAverages = curriculum.competencies
    .map((competency) => ({
      competency,
      average: Math.round(
        students.reduce(
          (sum, student) =>
            sum +
            (studentProgress[student.id]?.competencyEvidence[competency.id] ?? 0),
          0,
        ) / Math.max(1, students.length),
      ),
    }))
    .sort((a, b) => a.average - b.average);

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Learning Signals"
        title="See progress, mastery, and friction"
        detail="Class-wide summaries remain connected to individual evidence, attempts, and transparent support triggers."
        action={
          <Button variant="secondary" onClick={exportGradebook}>
            <Icon name="Download" size={17} />
            Export gradebook CSV
          </Button>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
        <Card className="p-7">
          <p className="font-retro text-[8px] tracking-widest text-cyan-300">
            REGIONAL COMPLETION
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">
            Where the class is exploring
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {regionAverages.map(({ region, average }) => (
              <div
                key={region.id}
                className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4"
              >
                <div className="mb-3 flex items-center gap-3">
                  <Icon name={region.icon} size={18} style={{ color: region.color }} />
                  <span className="text-xs font-bold text-slate-300">
                    {region.name}
                  </span>
                </div>
                <ProgressBar value={average} color="bg-indigo-400" />
                <p className="mt-2 text-right text-[9px] text-slate-600">
                  {average}% average
                </p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-7">
          <p className="font-retro text-[8px] tracking-widest text-amber-300">
            CONCEPT BOTTLENECKS
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">
            Lowest evidence areas
          </h2>
          <div className="mt-6 space-y-3">
            {competencyAverages.slice(0, 8).map(({ competency, average }) => (
              <div
                key={competency.id}
                className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-slate-200">
                      {competency.name}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-600">
                      {masteryFromEvidence(average)}
                    </p>
                  </div>
                  <span className="font-retro text-[9px] text-amber-300">
                    {average}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-slate-800 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-retro text-[8px] tracking-widest text-violet-300">
              STUDENT LOGS
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-white">
              Individual progress
            </h2>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-400">
            <input
              type="checkbox"
              checked={riskOnly}
              onChange={(event) => setRiskOnly(event.target.checked)}
              className="h-4 w-4 rounded border-slate-700 bg-slate-900"
            />
            Show support queue only
          </label>
        </div>
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead className="bg-slate-950/55 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4">Student</th>
                <th className="px-4 py-4">Guild</th>
                <th className="px-4 py-4">Completion</th>
                <th className="px-4 py-4">Mastery evidence</th>
                <th className="px-4 py-4">Attempts</th>
                <th className="px-4 py-4">Signal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {visible.map((item) => {
                const guild = GUILDS.find(
                  (entry) => entry.id === item.student.guild,
                );
                return (
                  <tr
                    key={item.student.id}
                    onClick={() => setSelectedId(item.student.id)}
                    className={`cursor-pointer transition hover:bg-slate-800/30 ${
                      selectedId === item.student.id ? "bg-indigo-500/5" : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-200">
                        {item.student.fullName}
                      </p>
                      <p className="mt-1 text-[10px] text-slate-600">
                        {item.student.email}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: guild?.color }}
                        />
                        {guild?.shortName}
                      </span>
                    </td>
                    <td className="px-4 py-4 font-bold text-slate-300">
                      {item.completion}%
                    </td>
                    <td className="px-4 py-4 font-bold text-slate-300">
                      {item.masteryAverage}
                    </td>
                    <td className="px-4 py-4 text-slate-400">{item.attempts}</td>
                    <td className="px-4 py-4">
                      <Pill
                        tone={item.riskReasons.length > 0 ? "rose" : "emerald"}
                      >
                        {item.riskReasons.length > 0
                          ? `${item.riskReasons.length} trigger(s)`
                          : "On track"}
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && selectedProgress && (
        <Card className="p-7">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-emerald-300">
                STUDENT DETAIL
              </p>
              <h2 className="mt-2 font-display text-3xl font-bold text-white">
                {selected.student.fullName}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Last active{" "}
                {new Date(selected.lastActiveAt).toLocaleDateString()} ·{" "}
                {selected.attempts} attempts
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.riskReasons.length > 0 ? (
                selected.riskReasons.map((reason) => (
                  <Pill key={reason} tone="rose">
                    {reason}
                  </Pill>
                ))
              ) : (
                <Pill tone="emerald">No active support triggers</Pill>
              )}
            </div>
          </div>
          <div className="mt-7 grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="text-sm font-extrabold text-slate-200">
                Competency evidence
              </h3>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {curriculum.competencies.slice(0, 10).map((competency) => {
                  const evidence =
                    selectedProgress.competencyEvidence[competency.id] ?? 0;
                  return (
                    <div
                      key={competency.id}
                      className="rounded-xl border border-slate-800 bg-slate-950/45 p-3"
                    >
                      <div className="flex justify-between gap-3 text-[10px]">
                        <span className="truncate font-bold text-slate-400">
                          {competency.name}
                        </span>
                        <span className="text-slate-600">{evidence}</span>
                      </div>
                      <div className="mt-2">
                        <ProgressBar value={evidence} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-slate-200">
                Recent progress log
              </h3>
              <div className="mt-4 space-y-2">
                {selectedProgress.attempts.slice(-6).reverse().map((attempt) => {
                  const quest = curriculum.quests.find(
                    (candidate) => candidate.id === attempt.questId,
                  );
                  return (
                    <div
                      key={attempt.id}
                      className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/45 p-3"
                    >
                      <Icon
                        name={attempt.status === "succeeded" ? "Check" : "Compass"}
                        size={17}
                        className={
                          attempt.status === "succeeded"
                            ? "text-emerald-300"
                            : "text-amber-300"
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-slate-300">
                          {quest?.title ?? attempt.questId}
                        </p>
                        <p className="mt-1 text-[9px] text-slate-600">
                          Attempt {attempt.attemptNumber} · {attempt.status}
                        </p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500">
                        +{attempt.awardedGradePoints} pts
                      </span>
                    </div>
                  );
                })}
                {selectedProgress.attempts.length === 0 && (
                  <p className="rounded-xl border border-dashed border-slate-800 p-5 text-center text-xs text-slate-600">
                    No quest attempts recorded yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
